/**
 * One mortgage, month by month: what was paid, what of it was interest, and
 * what was left owing afterwards.
 *
 * Everything here is the payments the totals were made from — nothing is
 * recomputed — so this is the page's audit trail as much as it is a view. That
 * is also why the figures carry pence: a column that has to add up cannot be
 * rounded to the pound.
 *
 * Years are the top level and months hang under them. A 35 year mortgage is 420
 * rows, which is a wall rather than a table; 35 rows that open is the same data
 * you can actually find your way around, and a year's totals are worth reading
 * on their own.
 */
import m from 'mithril';
import * as Plot from '@observablehq/plot';

import { asExactCurrency, asPercent, yearsMonthsLong } from './format.ts';
import { heading } from './hint.ts';
import { PlotFigure } from './plot.ts';
import { ScrollHint } from './scroll_hint.ts';
import type { Mortgage } from './lib/mortgage.ts';
import {
    length,
    monthlyCost,
    totalCost,
    totalInterest,
} from './lib/mortgage.ts';
import type { ScheduleMonth, ScheduleYear } from './lib/schedule.ts';
import { scheduleYears } from './lib/schedule.ts';

interface ScheduleAttrs {
    mortgage: Mortgage;
}

const INTEREST = 'Interest';
const OFF_LOAN = 'Off the loan';

export function Schedule(): m.Component<ScheduleAttrs> {
    const open = new Set<number>();
    let shown: Mortgage | null = null;

    return {
        view: ({ attrs: { mortgage } }) => {
            // A different mortgage is a different set of years, and year 7 of
            // the last one has nothing to say about year 7 of this one.
            if (mortgage !== shown) {
                shown = mortgage;
                open.clear();
            }

            const years = scheduleYears(mortgage);
            if (years.length === 0) {
                return m('p.prose', 'There is nothing to pay.');
            }

            return m(
                '.schedule',
                stats(mortgage),
                figures(mortgage, years),
                m(
                    ScrollHint,
                    { boxClass: 'table-scroll' },
                    m(
                        'table.data-table.schedule__table',
                        columns(),
                        m(
                            'tbody',
                            years.flatMap((year) => [
                                yearRow(year, open.has(year.year), () =>
                                    toggle(open, year.year),
                                ),
                                ...(open.has(year.year)
                                    ? year.months.map((month) =>
                                          monthRow(year, month),
                                      )
                                    : []),
                            ]),
                        ),
                        totals(mortgage),
                    ),
                ),
                m(
                    'p.schedule__note',
                    'Figures are rounded to the penny, so a row can differ from its parts by one.',
                ),
            );
        },
    };
}

function toggle(open: Set<number>, year: number) {
    if (!open.delete(year)) {
        open.add(year);
    }
}

function stats(mortgage: Mortgage) {
    return m('.schedule__stats', [
        stat('A month', asExactCurrency(monthlyCost(mortgage))),
        stat('Term', yearsMonthsLong(length(mortgage))),
        stat('Paid in all', asExactCurrency(totalCost(mortgage))),
        stat('Of that, interest', asExactCurrency(totalInterest(mortgage))),
    ]);
}

function stat(label: string, value: string) {
    return m('.schedule__stat', [
        m('.schedule__stat-label', label),
        m('.schedule__stat-value', value),
    ]);
}

function columns() {
    return m(
        'thead',
        m('tr', [
            m('th', 'Year'),
            heading(
                'Rate',
                'The annual rate charged. A year that spans the end of your fixed term shows both of them.',
                'left',
            ),
            heading('Paid', 'What you handed the lender.', 'left'),
            heading(
                'Interest',
                'The part of that payment the lender charged you for the loan. It buys you nothing.',
                'left',
            ),
            heading(
                'Off the loan',
                'The rest of the payment, which is the part that actually reduces what you owe.',
            ),
            heading('Still owed', 'What is left of the debt afterwards.'),
        ]),
    );
}

function yearRow(year: ScheduleYear, is_open: boolean, onclick: () => void) {
    return m(
        'tr.schedule__year',
        { key: `y${year.year}` },
        m(
            'td',
            m(
                'button.disclosure',
                {
                    type: 'button',
                    'aria-expanded': String(is_open),
                    onclick,
                },
                [
                    m('span.disclosure__mark', is_open ? '▾' : '▸'),
                    `Year ${year.year}`,
                ],
            ),
        ),
        m('td.numeric', year.rates.map(asPercent).join(' → ')),
        m('td.numeric', asExactCurrency(year.paid)),
        m('td.numeric', asExactCurrency(year.interest)),
        m('td.numeric', asExactCurrency(year.off_loan)),
        m('td.numeric', asExactCurrency(year.balance)),
    );
}

function monthRow(year: ScheduleYear, month: ScheduleMonth) {
    return m(
        'tr.schedule__month',
        { key: `y${year.year}m${month.month}` },
        m('td', [
            `Month ${month.month}`,
            // Otherwise the last row of a mortgage paid at a flat rate reads as
            // a payment gone missing rather than as the end of the debt.
            month.part_payment ? m('span.schedule__tag', 'part payment') : null,
        ]),
        m('td.numeric', asPercent(month.rate)),
        m('td.numeric', asExactCurrency(month.paid)),
        m('td.numeric', asExactCurrency(month.interest)),
        m('td.numeric', asExactCurrency(month.off_loan)),
        m('td.numeric', asExactCurrency(month.balance)),
    );
}

/** The line the whole table has to add up to. */
function totals(mortgage: Mortgage) {
    return m(
        'tfoot',
        m('tr', [
            m('td', 'In all'),
            m('td.numeric', ''),
            m(
                'td.numeric.numeric--total',
                asExactCurrency(totalCost(mortgage)),
            ),
            m(
                'td.numeric.numeric--total',
                asExactCurrency(totalInterest(mortgage)),
            ),
            m('td.numeric.numeric--total', asExactCurrency(mortgage.principle)),
            m('td.numeric', asExactCurrency(0)),
        ]),
    );
}

/**
 * The same two facts as the table, in the shape the table cannot show: the debt
 * falling away, and how little of an early payment goes anywhere near it.
 *
 * Two charts rather than one with two scales — £100,000 owed and £600 a month
 * do not share an axis. `data` is the mortgage itself, so PlotFigure redraws
 * when the mortgage changes and not on every keystroke elsewhere on the page.
 */
function figures(mortgage: Mortgage, years: ScheduleYear[]) {
    const months = years.flatMap((year) => year.months);
    const detail = mortgage.mortgage_detail;
    // The fixed rate covers `fixed_term * 12` payments, so the rule sits in the
    // gap after the last of them.
    const switches_at =
        detail.initial_interest !== detail.subsequent_interest
            ? detail.fixed_term * 12 + 0.5
            : null;
    const switch_month =
        switches_at !== null && switches_at < months.length
            ? switches_at
            : null;

    return m('.schedule__figures', [
        m('.schedule__figure', [
            m('.schedule__figure-head', [
                m('h3.schedule__figure-title', 'What you still owe'),
                switch_month === null ? null : legend([['rule', 'Fixed ends']]),
            ]),
            m(PlotFigure, {
                class: 'schedule__plot',
                data: mortgage,
                draw: (width, height) =>
                    balanceChart(months, switch_month, width, height),
            }),
        ]),
        m('.schedule__figure', [
            m('.schedule__figure-head', [
                m('h3.schedule__figure-title', 'Where each payment goes'),
                legend([
                    ['var(--negative)', INTEREST],
                    ['var(--accent)', OFF_LOAN],
                ]),
            ]),
            m(PlotFigure, {
                class: 'schedule__plot',
                data: mortgage,
                draw: (width, height) =>
                    splitChart(months, switch_month, width, height),
            }),
        ]),
    ]);
}

/**
 * The key, written here rather than left to Plot: Plot's own legend comes back
 * wrapped in a figure that adds its own height, and PlotFigure sizes the chart
 * from the box it is given.
 */
function legend(items: [string, string][]) {
    return m(
        '.schedule__key',
        items.map(([colour, label]) =>
            m('.schedule__key-item', [
                m('span.schedule__key-swatch', {
                    class:
                        colour === 'rule' ? 'schedule__key-swatch--rule' : '',
                    style: colour === 'rule' ? {} : { background: colour },
                }),
                label,
            ]),
        ),
    );
}

/** Shared by both charts, so they read as one pair rather than two drawings. */
function frame(
    months: ScheduleMonth[],
    width: number,
    height: number,
    // The gutter has to hold the widest tick and the rotated label beside it,
    // and "100k" and "1,500" are not the same width, so the two charts differ.
    // It does not shrink on a narrow screen: the label overlapping the ticks is
    // worse than a chart that is a little narrower.
    gutter = 58,
): Plot.PlotOptions {
    return {
        width,
        height,
        marginTop: 12,
        marginRight: 12,
        marginBottom: 34,
        marginLeft: gutter,
        style: {
            background: 'transparent',
            color: 'var(--ink-soft)',
            fontFamily: 'inherit',
            fontSize: '11px',
        },
        x: {
            domain: [1, Math.max(months.length, 2)],
            // The data is months, because that is what a payment is, but a
            // count of months is not a length anyone reads: the ticks are whole
            // years, labelled as years.
            label: 'Year',
            labelAnchor: 'center',
            labelArrow: 'none',
            labelOffset: 30,
            // Roughly a label every 80px, which is the width these charts
            // actually get: two of them side by side is ~430px each, not the
            // full width the term comparison graph runs at.
            ticks: yearTicks(
                months.length,
                Math.max(2, Math.floor(width / 80)),
            ),
            tickFormat: (month: number) => String(Math.round(month / 12)),
        },
    };
}

/**
 * Whole years, at the coarsest of 1, 2, 5, 10 that keeps the axis under `most`
 * ticks. A 35 year mortgage every 5 years, a 4 year one every year — a fixed
 * step would crowd the long ones and leave the short ones with two labels.
 */
function yearTicks(months: number, most: number) {
    const years = Math.floor(months / 12);
    const step = [1, 2, 5, 10].find((each) => years / each <= most) ?? 10;
    const ticks = [];

    for (let year = step; year <= years; year += step) {
        ticks.push(year * 12);
    }

    return ticks;
}

function axes() {
    return [
        Plot.frame({ anchor: 'bottom', stroke: 'var(--line-strong)' }),
        Plot.frame({ anchor: 'left', stroke: 'var(--line-strong)' }),
    ];
}

/**
 * "Year 4, month 47" — the axis ticks in years and the table is in months, so a
 * tip that named only one of them would leave you counting.
 */
function when(month: ScheduleMonth) {
    return `Year ${Math.ceil(month.month / 12)}, month ${month.month}`;
}

/** Where the fixed rate runs out, in both charts, so they line up. */
function switchMark(month: number | null) {
    return month === null
        ? []
        : [
              Plot.ruleX([month], {
                  stroke: 'var(--ink-faint)',
                  strokeDasharray: '3,3',
              }),
          ];
}

function balanceChart(
    months: ScheduleMonth[],
    switch_month: number | null,
    width: number,
    height: number,
) {
    return Plot.plot({
        ...frame(months, width, height),
        y: {
            label: 'Still owed',
            labelAnchor: 'center',
            labelArrow: 'none',
            tickFormat: thousands,
            ticks: 4,
        },
        marks: [
            Plot.areaY(months, {
                x: 'month',
                y: 'balance',
                fill: 'var(--accent-wash)',
            }),
            Plot.lineY(months, {
                x: 'month',
                y: 'balance',
                stroke: 'var(--accent)',
                strokeWidth: 1.5,
            }),
            ...switchMark(switch_month),
            ...axes(),
            Plot.tip(
                months,
                Plot.pointerX({
                    x: 'month',
                    y: 'balance',
                    title: (d: ScheduleMonth) =>
                        `${when(d)}\n${asExactCurrency(d.balance)} still owed`,
                    fill: 'var(--ink)',
                    stroke: 'var(--ink)',
                    lineHeight: 1.3,
                }),
            ),
        ],
    });
}

function splitChart(
    months: ScheduleMonth[],
    switch_month: number | null,
    width: number,
    height: number,
) {
    // Long form, interest first, so the stack puts it at the bottom against the
    // axis where it can be read off.
    const parts = months.flatMap((month) => [
        { month: month.month, part: INTEREST, amount: month.interest },
        { month: month.month, part: OFF_LOAN, amount: month.off_loan },
    ]);

    return Plot.plot({
        ...frame(months, width, height, 66),
        y: {
            label: 'A month',
            labelAnchor: 'center',
            labelArrow: 'none',
            ticks: 4,
        },
        color: {
            domain: [INTEREST, OFF_LOAN],
            range: ['var(--negative)', 'var(--accent)'],
        },
        marks: [
            Plot.areaY(parts, {
                x: 'month',
                y: 'amount',
                fill: 'part',
                fillOpacity: 0.85,
            }),
            ...switchMark(switch_month),
            ...axes(),
            Plot.tip(
                months,
                Plot.pointerX({
                    x: 'month',
                    y: 'paid',
                    title: (d: ScheduleMonth) =>
                        `${when(d)}\n` +
                        `${asExactCurrency(d.interest)} interest · ` +
                        `${asExactCurrency(d.off_loan)} off the loan`,
                    fill: 'var(--ink)',
                    stroke: 'var(--ink)',
                    lineHeight: 1.3,
                }),
            ),
        ],
    });
}

/** 140000 as 140k, matching the term comparison graph. */
function thousands(value: number) {
    return value === 0 ? '0' : `${Math.round(value / 1000)}k`;
}
